import { readableColor } from 'polished';
import { colors } from '@ardoq/design-tokens';
import type { ArdoqIcon, RepresentationData } from '@ardoq/data-model';
import { IconName } from '@ardoq/icons';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import {
  MODERNIZED_BLOCK_DIAGRAM_FONT_SIZE as FONT_SIZE,
  MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT as LINE_HEIGHT,
} from '@ardoq/graph-labeling';
import { getGroupExpanderRect, type LayoutBounds } from '@ardoq/graph-view';
import {
  MODERNIZED_BLOCK_DIAGRAM_NODE_WIDTH as MIN_GROUP_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_CORNER_RADIUS as CORNER_RADIUS,
  MODERNIZED_BLOCK_DIAGRAM_NODE_BORDER_WIDTH as NODE_BORDER_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_SHAPE_HEIGHT as SHAPE_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_NODE_LABEL_MARGIN as LABEL_MARGIN,
  MODERNIZED_BLOCK_DIAGRAM_COLLAPSED_COMPONENT_GROUP_HEIGHT as COLLAPSED_COMPONENT_GROUP_HEIGHT,
} from 'yfilesExtensions/styles/modernized/consts';
import {
  MODERNIZED_BLOCK_DIAGRAM_GROUP_PADDING as PADDING,
  MODERNIZED_BLOCK_DIAGRAM_GROUP_HEADER_HEIGHT as HEADER_CONTENT_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_GROUP_HEADER_ICON_HEIGHT as ICON_SIZE,
  MODERNIZED_BLOCK_DIAGRAM_COLLAPSED_COMPONENT_GROUP_STRIPE_HEIGHT as COLLAPSED_STRIPE_HEIGHT,
} from 'tabview/blockDiagram/view/yFilesExtensions/modernized/consts';
import { paintIcon } from 'tabview/canvasRendering/paintIcon';
import { paintImageInCircle } from 'tabview/canvasRendering/paintImageInCircle';
import { drawSpriteIcon } from 'tabview/blocks/visual/node/drawSpriteIcon';
import { drawCollapsedComponentGroup } from 'yfilesExtensions/styles/modernized/leafNode/drawCollapsedComponentGroup';

/**
 * Renders a group as the canonical Ardoq group (Pei-Yu's Modernized Block Diagram design)
 * directly to canvas. Expanded groups (and collapsed non-component groups) render as a grey
 * rounded body with a type-colored header stripe carrying the representation + a truncated
 * label ({@link drawGroupContainer} / {@link drawCollapsedGroupHeader}). Collapsed component
 * groups render as the canonical collapsed card ({@link drawCollapsedComponentGroupCard}),
 * shared with the block diagram. In all cases the expander chevron is painted at
 * graph-view's fold hit-region (`getGroupExpanderRect`).
 */

const LABEL_FONT = `${FONT_SIZE}px ${ARDOQ_DEFAULT_FONT_FAMILY}`;
const ELLIPSIS = '…';

/** Height of the colored header stripe drawn along the top of the group. */
export const HEADER_STRIPE_HEIGHT = HEADER_CONTENT_HEIGHT + 2 * PADDING;

/**
 * Insets the layout reserves inside a group for its children: the header stripe
 * plus a small gap at the top, and uniform padding on the other sides.
 */
export const GROUP_INSETS = {
  top: HEADER_STRIPE_HEIGHT + PADDING,
  right: PADDING,
  bottom: PADDING,
  left: PADDING,
};

/** Minimum group size (the layout grows the group to fit its children). */
export const MIN_GROUP_SIZE = {
  width: MIN_GROUP_WIDTH,
  height: HEADER_STRIPE_HEIGHT,
};

export type GroupContainer = {
  /**
   * Component-type representation drawn at the left of the stripe. Absent for
   * non-type groups (e.g. field-value groups), which show just the label.
   */
  representation?: { icon: ArdoqIcon; isImage: boolean; imageValue: string };
  /** Header stripe fill — the resolved component-type color (or a neutral default). */
  color: string;
  /** Group label; truncated to a single line at draw time. */
  label: string;
};

/** Truncates `text` with an ellipsis so it fits within `maxWidth` for the active font. */
const truncateToWidth = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string => {
  if (maxWidth <= 0) return '';
  if (ctx.measureText(text).width <= maxWidth) return text;
  let truncated = text;
  while (
    truncated.length > 0 &&
    ctx.measureText(truncated + ELLIPSIS).width > maxWidth
  ) {
    truncated = truncated.slice(0, -1);
  }
  return truncated.length > 0 ? truncated + ELLIPSIS : ELLIPSIS;
};

/** Path for the header stripe: rounded top corners, square bottom edge. */
const stripePath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  stripeHeight: number
): void => {
  ctx.beginPath();
  ctx.moveTo(x + CORNER_RADIUS, y);
  ctx.lineTo(x + width - CORNER_RADIUS, y);
  ctx.arcTo(x + width, y, x + width, y + CORNER_RADIUS, CORNER_RADIUS);
  ctx.lineTo(x + width, y + stripeHeight);
  ctx.lineTo(x, y + stripeHeight);
  ctx.lineTo(x, y + CORNER_RADIUS);
  ctx.arcTo(x, y, x + CORNER_RADIUS, y, CORNER_RADIUS);
  ctx.closePath();
};

/**
 * Grey body + type-colored header stripe carrying the representation, an expander chevron
 * (its glyph passed in), and a truncated label. Used for expanded groups (UNFOLD_LESS) and
 * collapsed non-component groups (UNFOLD_MORE) — the chevron is the only difference, so it's
 * a parameter rather than a branch.
 */
const paintGroupHeaderBar = (
  ctx: CanvasRenderingContext2D,
  layout: LayoutBounds,
  container: GroupContainer,
  expanderIcon: IconName
): void => {
  const { x, y, width, height } = layout;
  const { color, representation, label } = container;
  const stripeHeight = Math.min(HEADER_STRIPE_HEIGHT, height);

  ctx.save();

  // Body: grey rounded rectangle.
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, CORNER_RADIUS);
  ctx.fillStyle = colors.grey50;
  ctx.fill();

  // Header stripe: type-colored band with rounded top corners.
  stripePath(ctx, x, y, width, stripeHeight);
  ctx.fillStyle = color;
  ctx.fill();

  // Border drawn last so it sits crisply over the body fill and the stripe edge.
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, CORNER_RADIUS);
  ctx.lineWidth = NODE_BORDER_WIDTH;
  ctx.strokeStyle = colors.grey400;
  ctx.stroke();

  const contentColor = readableColor(color, colors.white950, colors.grey950);
  const stripeCy = y + stripeHeight / 2;

  // Expander chevron: graph-view owns the hit-region (getGroupExpanderRect) and
  // toggles folding when it's clicked; we just paint the canonical chevron there.
  const expander = getGroupExpanderRect(layout);
  drawSpriteIcon(
    expanderIcon,
    expander.width,
    contentColor,
    expander.x + expander.width / 2,
    expander.y + expander.height / 2,
    ctx
  );

  const contentStartX = expander.x + expander.width + PADDING;

  // Representation circle to the right of the expander (type groups only).
  if (representation) {
    const iconCx = contentStartX + ICON_SIZE / 2;
    if (representation.isImage && representation.imageValue) {
      paintImageInCircle(
        ctx,
        representation.imageValue,
        iconCx,
        stripeCy,
        ICON_SIZE / 2,
        ICON_SIZE
      );
    } else {
      paintIcon(
        ctx,
        iconCx,
        stripeCy,
        ICON_SIZE,
        contentColor,
        representation.icon
      );
    }
  }

  // Label: single, truncated line vertically centered in the stripe. Starts after
  // the representation circle when present, else right after the expander.
  const labelStartX = representation
    ? contentStartX + ICON_SIZE + PADDING
    : contentStartX;
  const maxLabelWidth = x + width - PADDING - labelStartX;
  ctx.font = LABEL_FONT;
  ctx.fillStyle = contentColor;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(
    truncateToWidth(ctx, label, maxLabelWidth),
    labelStartX,
    stripeCy
  );

  ctx.restore();
};

/** Expanded group container (header stripe + children below). */
export const drawGroupContainer = (
  ctx: CanvasRenderingContext2D,
  layout: LayoutBounds,
  container: GroupContainer
): void => paintGroupHeaderBar(ctx, layout, container, IconName.UNFOLD_LESS);

/** Collapsed non-component group: a header-only bar with an expand chevron. */
export const drawCollapsedGroupHeader = (
  ctx: CanvasRenderingContext2D,
  layout: LayoutBounds,
  container: GroupContainer
): void => paintGroupHeaderBar(ctx, layout, container, IconName.UNFOLD_MORE);

/**
 * Card footprint + representation offset for a collapsed component group, mirroring the
 * block diagram's `collapsedComponentGroupShapeY`: the content (representation + wrapped
 * label) is vertically centered in the body below the header stripe.
 */
export const layoutCollapsedComponentGroup = (
  labelLines: string[]
): { height: number; shapeY: number } => {
  const labelHeight = labelLines.length * LINE_HEIGHT;
  const contentHeight =
    SHAPE_HEIGHT + (labelHeight ? LABEL_MARGIN + labelHeight : 0);
  const centerY =
    COLLAPSED_STRIPE_HEIGHT +
    (COLLAPSED_COMPONENT_GROUP_HEIGHT - COLLAPSED_STRIPE_HEIGHT) / 2;
  return {
    height: COLLAPSED_COMPONENT_GROUP_HEIGHT,
    shapeY: centerY - contentHeight / 2,
  };
};

export type CollapsedComponentGroupCard = {
  representation: RepresentationData;
  /** Resolved component-type color. */
  color: string;
  /** Pre-wrapped label lines (≤ 3). */
  labelLines: string[];
  /** Representation offset from {@link layoutCollapsedComponentGroup}. */
  shapeY: number;
};

/**
 * Collapsed component group: the canonical collapsed card (shared with the block diagram)
 * plus a centered, wrapped label below the representation.
 */
export const drawCollapsedComponentGroupCard = (
  ctx: CanvasRenderingContext2D,
  layout: LayoutBounds,
  { representation, color, labelLines, shapeY }: CollapsedComponentGroupCard
): void => {
  drawCollapsedComponentGroup(ctx, layout, {
    representation,
    componentColor: color,
    shapeY,
    expander: getGroupExpanderRect(layout),
    isConditionallyFormatted: false,
    isTransparentized: false,
  });

  const { x, y, width } = layout;
  ctx.save();
  ctx.font = LABEL_FONT;
  ctx.fillStyle = colors.black950;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const cx = x + width / 2;
  const topLineY = y + shapeY + SHAPE_HEIGHT + LABEL_MARGIN + LINE_HEIGHT / 2;
  labelLines.forEach((line, lineIndex) => {
    ctx.fillText(line, cx, topLineY + LINE_HEIGHT * lineIndex);
  });
  ctx.restore();
};
